import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MyPartners } from "./MyPartners";
import { BrowseChallenges } from "./BrowseChallenges";
import { ProgressFeed } from "./ProgressFeed";
import { AccountabilityGuide } from "./AccountabilityGuide";
import { NotificationBell } from "./NotificationBell";
import { PartnerMatching } from "./PartnerMatching";
import { DirectMessaging } from "./DirectMessaging";
import { GamificationDashboard } from "./GamificationDashboard";
import { ChallengeTemplates } from "./ChallengeTemplates";
import { SuccessStories } from "./SuccessStories";
import { ChallengesOverview } from "@/components/challenges/ChallengesOverview";
import { Users, Trophy, Compass, Activity, MessageSquare, Zap, Star, Sparkles } from "lucide-react";

interface AccountabilityHubProps {
  initialTab?: string;
  filterCategory?: string;
}

export function AccountabilityHub({ initialTab = "partners", filterCategory }: AccountabilityHubProps) {
  return (
    <div className="space-y-4 sm:space-y-6">
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex-1 space-y-1">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-secondary" />
                Accountability & Challenges
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm leading-relaxed">
                Connect with others for mutual support, join challenges, and celebrate wins together. 
                Studies show 65-95% better adherence with accountability partners.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <NotificationBell />
              <AccountabilityGuide />
            </div>
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue={initialTab} className="space-y-4">
        <TabsList className="flex w-full h-auto overflow-x-auto scrollbar-hide gap-1 p-1.5 justify-start md:justify-center">
          <TabsTrigger value="partners" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Users className="h-4 w-4" />
            <span className="text-[11px]">Partners</span>
          </TabsTrigger>
          <TabsTrigger value="active" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Trophy className="h-4 w-4" />
            <span className="text-[11px]">Challenges</span>
          </TabsTrigger>
          <TabsTrigger value="browse" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Compass className="h-4 w-4" />
            <span className="text-[11px]">Browse</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Sparkles className="h-4 w-4" />
            <span className="text-[11px]">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="feed" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Activity className="h-4 w-4" />
            <span className="text-[11px]">Feed</span>
          </TabsTrigger>
          <TabsTrigger value="messages" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <MessageSquare className="h-4 w-4" />
            <span className="text-[11px]">Messages</span>
          </TabsTrigger>
          <TabsTrigger value="achievements" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Zap className="h-4 w-4" />
            <span className="text-[11px]">Points</span>
          </TabsTrigger>
          <TabsTrigger value="stories" className="gap-1.5 flex-col py-2.5 px-3 min-w-[72px]">
            <Star className="h-4 w-4" />
            <span className="text-[11px]">Stories</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="space-y-4">
          <PartnerMatching />
          <MyPartners />
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          <ChallengesOverview />
        </TabsContent>

        <TabsContent value="browse" className="space-y-4">
          <BrowseChallenges filterCategory={filterCategory} />
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <ChallengeTemplates />
        </TabsContent>

        <TabsContent value="feed" className="space-y-4">
          <ProgressFeed />
        </TabsContent>

        <TabsContent value="messages" className="space-y-4">
          <DirectMessaging />
        </TabsContent>

        <TabsContent value="achievements" className="space-y-4">
          <GamificationDashboard />
        </TabsContent>

        <TabsContent value="stories" className="space-y-4">
          <SuccessStories />
        </TabsContent>
      </Tabs>
    </div>
  );
}
